import { defineStore } from 'pinia'
import type { ColorChoice } from '~/composables/shapes/colors'
import { useStudioColors } from '~/composables/useStudioColors'

const STORAGE_KEY = 'feltright:recent-colors'
const MAX_RECENTS = 4

interface RecentColorsState {
  /**
   * Most-recent-first list of color labels. We persist just the labels and
   * resolve them against the live studio-color catalog at render time so a
   * color removed or deactivated server-side drops out of the recents
   * without needing a separate cleanup pass.
   */
  colorLabels: string[]
  /**
   * Whether the store has finished hydrating from localStorage. Used by
   * the mobile drawer so the Recent row doesn't flicker empty before the
   * client-side storage read completes.
   */
  hydrated: boolean
}

export const useRecentColorsStore = defineStore('recent-colors', {
  state: (): RecentColorsState => ({
    colorLabels: [],
    hydrated: false,
  }),

  getters: {
    /**
     * Resolves stored labels through the live studio-color catalog and
     * drops any labels that no longer resolve (e.g. deactivated server-side
     * or replaced). The mobile drawer consumes this directly.
     */
    recentColorChoices(state): ColorChoice[] {
      const { getColors } = useStudioColors()
      const catalog = getColors()
      return state.colorLabels
        .map((label) => catalog.find((c) => c.label === label))
        .filter((c): c is ColorChoice => Boolean(c) && c.active !== false)
    },
  },

  actions: {
    /**
     * Read persisted recents from localStorage. Safe to call multiple times;
     * subsequent calls are a no-op once `hydrated` is true.
     */
    hydrate() {
      if (this.hydrated) return
      if (import.meta.client) {
        try {
          const raw = localStorage.getItem(STORAGE_KEY)
          if (raw) {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) {
              this.colorLabels = parsed.filter((v) => typeof v === 'string').slice(0, MAX_RECENTS)
            }
          }
        } catch (e) {
          // Corrupt entry, quota error, or SSR — fall back to empty list.
          console.warn('Failed to hydrate recent colors:', e)
        }
      }
      this.hydrated = true
    },

    /**
     * Move the tapped label to the front of the recents and trim to
     * MAX_RECENTS. No-op if the label is empty or already in the front.
     */
    addRecentColor(label: string) {
      if (!label || typeof label !== 'string') return
      const next = [label, ...this.colorLabels.filter((l) => l !== label)].slice(0, MAX_RECENTS)
      // Skip the write if the order hasn't actually changed (same label
      // tapped twice in a row, already at the front).
      if (
        next.length === this.colorLabels.length &&
        next.every((l, i) => l === this.colorLabels[i])
      ) {
        return
      }
      this.colorLabels = next
      this.persist()
    },

    /** Clear all recents. Mostly here for tests / future settings UI. */
    clearRecent() {
      this.colorLabels = []
      this.persist()
    },

    persist() {
      if (!import.meta.client) return
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.colorLabels))
      } catch (e) {
        // Quota, Safari Private Mode, or storage disabled — silently drop.
        console.warn('Failed to persist recent colors:', e)
      }
    },
  },
})
